import { ComponentRef, Injectable, Injector, Optional, SkipSelf, TemplateRef } from '@angular/core';

import { EMPTY, Observable, Subject, zip } from 'rxjs';
import { first, map } from 'rxjs/operators';

import {
  ComponentPortal,
  ComponentType,
  OverlayRef,
  OverlayService,
  OverlayState,
  TemplatePortal,
} from '../core/core.module';
import { KeyCode } from '../core/keycode';
import { buildDialogConfig, DialogConfig, DialogConfigOptions } from './dialog-config';
import { DialogContainerComponent } from './dialog-container/dialog-container.component';
import { DialogInjector } from './dialog-injector';
import { DialogRef } from './dialog-ref';
import { DialogComponentI } from './dialog.component';

@Injectable({ providedIn: 'root' })
export class DialogOpenerService {
  /** An observable that is notified when a dialog has been opened. */
  public afterOpen: Observable<DialogRef>;
  /** An observable that is notified when all open dialog have finished closing. */
  public afterAllClosed: Observable<void>;

  private openDialogsAtThisLevel: DialogRef[] = [];
  private afterAllClosedAtThisLevel = new Subject<void>();
  private afterOpenAtThisLevel = new Subject<DialogRef>();
  private boundKeydown: any = this.handleKeydown.bind(this);
  private activeKeyboardEvents: boolean = true;

  /** Keeps track of the currently-open dialogs. */
  public get openDialogs(): DialogRef[] {
    return this.parentDialogOpener
      ? this.parentDialogOpener.openDialogs
      : this.openDialogsAtThisLevel;
  }

  /** Subject for notifying the user that a dialog has opened. */
  private get afterOpenSubject(): Subject<DialogRef> {
    return this.parentDialogOpener
      ? this.parentDialogOpener.afterOpenSubject
      : this.afterOpenAtThisLevel;
  }

  /** Subject for notifying the user that all open dialogs have finished closing. */
  private get afterAllClosedSubject(): Subject<void> {
    return this.parentDialogOpener
      ? this.parentDialogOpener.afterAllClosedSubject
      : this.afterAllClosedAtThisLevel;
  }

  constructor(
    private overlayService: OverlayService,
    private injector: Injector,
    @Optional()
    @SkipSelf()
    public parentDialogOpener: DialogOpenerService,
  ) {
    this.afterOpen = this.afterOpenSubject.asObservable();
    this.afterAllClosed = this.afterAllClosedSubject.asObservable();
  }

  public get hasDialogsOpen(): boolean {
    return this.openDialogs.length > 0;
  }

  public isTop(dialogRef: DialogRef): boolean {
    return this.openDialogs[this.openDialogs.length - 1] === dialogRef;
  }

  public open<T>(
    componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
    configOptions?: DialogConfigOptions,
  ): DialogRef<T> {
    const config = buildDialogConfig(configOptions);
    const injector = this.getDialogParentInjector(config);

    config.level = injector.get('overlayLevel') + 1;
    const dialogContainerInjector = Injector.create({
      providers: [{ provide: 'overlayLevel', useValue: config.level }],
      parent: injector,
    });

    const overlayRef = this.createOverlay(config);
    const dialogContainer = this.attachDialogContainer(overlayRef, config, dialogContainerInjector);
    const dialogRefInjector = this.provideDialogRefInjector(config, injector);

    const dialogRef = this.attachDialogContent(
      componentOrTemplateRef,
      dialogContainer,
      overlayRef,
      config,
      dialogRefInjector,
    );

    if (!this.openDialogs.length) {
      document.addEventListener('keydown', this.boundKeydown);
    }

    this.openDialogs.push(dialogRef);

    dialogRef.afterClosed.subscribe(() => this.removeOpenDialog(dialogRef));
    this.afterOpenSubject.next(dialogRef);

    return dialogRef;
  }

  /**
   * Closes all of the currently-open dialogs.
   */
  public closeAll(): Observable<boolean> {
    if (this.openDialogs.length > 0) {
      const dialogCloseObservables = this.openDialogs.reverse().map(
        ref =>
          new Observable(observer => {
            ref.afterClosed.subscribe({
              complete: () => {
                observer.next(ref);
                observer.complete();
              },
            });
            ref.close();
          }),
      );

      return zip(...dialogCloseObservables).pipe(map(() => true));
    } else {
      return EMPTY;
    }
  }

  public setKeyboardEventsHandler(handleKeyboardEvents: boolean): void {
    this.activeKeyboardEvents = handleKeyboardEvents;
  }

  public getOverlayLevel(): number {
    const dialogRef = <DialogRef | undefined>this.openDialogs[this.openDialogs.length - 1];
    return dialogRef?.overlayRef.getOverlayLevel() || 0;
  }

  /**
   * Creates the overlay into which the dialog will be loaded.
   * @param dialogConfig The dialog configuration.
   * @returns A promise resolving to the OverlayRef for the created overlay.
   */
  private createOverlay(dialogConfig: DialogConfig): OverlayRef {
    const overlayState = this.getOverlayState(dialogConfig);
    return this.overlayService.create(overlayState);
  }

  /**
   * Attaches an RtDialogContainer to a dialog's already-created overlay.
   * @param overlay Reference to the dialog's underlying overlay.
   * @param config The dialog configuration.
   * @returns A promise resolving to a ComponentRef for the attached container.
   */
  private attachDialogContainer(
    overlay: OverlayRef,
    config: DialogConfig,
    injector: Injector,
  ): DialogContainerComponent {
    const viewContainerRef = config ? config.viewContainerRef : undefined;
    const containerPortal = new ComponentPortal(
      DialogContainerComponent,
      viewContainerRef,
      injector,
    );

    const containerRef: ComponentRef<DialogContainerComponent> = overlay.attach(containerPortal);
    containerRef.instance.dialogConfig = config;
    if (config.containerOptions && config.containerOptions.hasPadding !== undefined) {
      containerRef.instance.hasPadding = config.containerOptions.hasPadding;
    }

    // Init lifecycle hooks on the created component
    containerRef.changeDetectorRef.detectChanges();

    return containerRef.instance;
  }

  /**
   * Attaches the user-provided component to the already-created RtDialogContainer.
   * @param componentOrTemplateRef The type of component being loaded into the dialog,
   *     or a TemplateRef to instantiate as the content.
   * @param dialogContainer Reference to the wrapping RtDialogContainer.
   * @param overlayRef Reference to the overlay in which the dialog resides.
   * @param config The dialog configuration.
   * @returns A promise resolving to the RtDialogRef that should be returned to the user.
   */
  // eslint-disable-next-line max-lines-per-function
  private attachDialogContent<T>(
    componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
    dialogContainer: DialogContainerComponent,
    overlayRef: OverlayRef,
    config: DialogConfig,
    injector: Injector,
  ): DialogRef<T> {
    // Create a reference to the dialog we're creating in order to give the user a handle
    // to modify and close it.
    const dialogRef = new DialogRef<T>(overlayRef, dialogContainer);

    if (!config.disableClose) {
      // When the dialog backdrop is clicked, we want to close it.
      overlayRef
        .backdropClick()
        .pipe(first())
        .subscribe(() => dialogRef.close());
    }

    // We create an injector specifically for the component we're instantiating so that it can
    // inject the RtDialogRef. This allows a component loaded inside of a dialog to close itself
    // and, optionally, to return a value.

    const dialogInjector = new DialogInjector(
      injector,
      dialogRef,
      dialogContainer.scrollable,
      config ? (<any>config).data : undefined,
      config.level,
    );

    overlayRef.overlayElement.classList.add('rt-overlay-pane--has-dialog-container');

    if (componentOrTemplateRef instanceof TemplateRef) {
      dialogContainer.attachTemplatePortal(new TemplatePortal(componentOrTemplateRef, null));
    } else {
      const contentRef = dialogContainer.attachComponentPortal(
        new ComponentPortal(
          componentOrTemplateRef,
          undefined,
          dialogInjector,
          config.componentFactoryResolver,
          config.environmentInjector,
        ),
      );

      dialogRef.componentInstance = contentRef.instance;
      if ((<any>dialogRef.componentInstance).setDialogData !== undefined) {
        (<DialogComponentI<any, any>>(<any>dialogRef.componentInstance)).setDialogData(
          config.data,
          true,
        );
      }

      /**
       * Inform Angular that a component has been created
       * - this will trigger lifecycle hooks and detectChanges on the created component
       */
      contentRef.changeDetectorRef.detectChanges();
    }

    return dialogRef;
  }

  /**
   * Creates an overlay state from a dialog config.
   * @param dialogConfig The dialog configuration.
   * @returns The overlay configuration.
   */
  private getOverlayState(dialogConfig: DialogConfig): OverlayState {
    const state = new OverlayState();
    const strategy = this.overlayService.position().global();

    state.backdropClass =
      dialogConfig.backdropStyle === 'default'
        ? state.backdropClass
        : 'rt-overlay-light-cover-backdrop';

    state.level = dialogConfig.level;
    state.hasBackdrop = true;
    state.positionStrategy = strategy;
    strategy.centerHorizontally();
    strategy.centerVertically();
    return state;
  }

  /**
   * Removes a dialog from the array of open dialogs.
   * @param dialogRef Dialog to be removed.
   */
  private removeOpenDialog(dialogRef: DialogRef): void {
    const index = this.openDialogs.indexOf(dialogRef);

    if (index > -1) {
      this.openDialogs.splice(index, 1);

      // no open dialogs are left, call next on afterAllClosed Subject
      if (!this.openDialogs.length) {
        this.afterAllClosedSubject.next();
        document.removeEventListener('keydown', this.boundKeydown);
      }
    }
  }

  /**
   * Handles global key presses while there are open dialogs. Closes the
   * top dialog when the user presses escape.
   */
  private handleKeydown(event: KeyboardEvent): void {
    if (event.defaultPrevented || !this.activeKeyboardEvents) {
      return;
    }

    const topDialog = this.openDialogs[this.openDialogs.length - 1];

    if (
      event.keyCode === KeyCode.Escape &&
      topDialog &&
      !topDialog.containerInstance.dialogConfig.disableClose
    ) {
      topDialog.close();
    }
  }

  private getDialogParentInjector(config: DialogConfig): Injector {
    return (
      config.injector ||
      (config.viewContainerRef && config.viewContainerRef.injector) ||
      this.injector
    );
  }

  private provideDialogRefInjector(config: DialogConfig, parentInjector: Injector): Injector {
    return Injector.create({
      providers: config.providers || [],
      parent: parentInjector,
    });
  }
}
